import { useState } from "react";
import {
  FileText,
  Trash,
  Plus,
  Warning,
  ArrowClockwise,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  useUserTemplates,
  useDeleteTemplate,
} from "@/lib/queries/user/useTemplates";
import { useToast } from "@/hooks/use-toast";
import { motion } from "motion/react";
import { TemplateUploadModal } from "@/components/TemplateUploadModal";
import { Link } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import useAppLinks from "@/lib/hooks/useAppLinks";
import { Trans, useTranslation } from "react-i18next";

const getBadgeStyles = (category?: string, subcategory?: string) => {
  if (!category) return "text-gray-700 bg-gray-100";

  switch (category) {
    case "Resource":
      return "text-blue-800 bg-blue-100 border-blue-200";
    case "Communication":
      switch (subcategory) {
        case "Parents":
          return "text-green-800 bg-green-100 border-green-200";
        case "Students":
          return "text-purple-800 bg-purple-100 border-purple-200";
        case "School":
          return "text-orange-800 bg-orange-100 border-orange-200";
        case "Other":
          return "text-pink-800 bg-pink-100 border-pink-200";
        default:
          return "text-emerald-800 bg-emerald-100 border-emerald-200";
      }
    default:
      return "text-gray-700 bg-gray-100";
  }
};

function TemplateCardSkeleton() {
  return (
    <Card className="transition-shadow bg-white border-gray-200 shadow-sm">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 bg-gray-200 rounded animate-pulse" />
            <div className="w-24 h-4 bg-gray-200 rounded animate-pulse" />
          </div>
          <div className="w-8 h-8 bg-gray-200 rounded animate-pulse" />
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="flex flex-wrap gap-1">
          <div className="w-16 h-6 bg-gray-200 rounded-full animate-pulse" />
          <div className="w-20 h-6 bg-gray-200 rounded-full animate-pulse" />
          <div className="w-12 h-6 bg-gray-200 rounded-full animate-pulse" />
        </div>
      </CardContent>
    </Card>
  );
}

function TemplateCard({
  template,
  onDelete,
}: {
  template: any;
  onDelete: (id: string) => void;
}) {
  const { t } = useTranslation("templates");
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await onDelete(template.id);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.2 }}
    >
      <Card className="transition-shadow bg-white border-gray-200 shadow-sm hover:shadow-md">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-gray-600" />
              <CardTitle className="text-sm font-medium text-gray-900">
                {template.name}
              </CardTitle>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleDelete}
              disabled={isDeleting}
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              {isDeleting ? (
                <div className="w-4 h-4 border-2 border-red-600 rounded-full animate-spin border-t-transparent" />
              ) : (
                <Trash className="w-4 h-4" />
              )}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="flex flex-wrap gap-1">
            {template.tags
              .slice(0, 3)
              .map(
                (tag: {
                  id: string;
                  tagText: string;
                  category?: string;
                  subcategory?: string;
                }) => {
                  return (
                    <Badge
                      key={tag.id}
                      variant="outline"
                      className={`${getBadgeStyles(tag.category, tag.subcategory)} text-xs font-medium border`}
                    >
                      {tag.tagText}
                    </Badge>
                  );
                }
              )}
            {template.tags.length > 6 && (
              <Dialog>
                <DialogTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="!p-2 !text-xs !font-medium rounded-full"
                  >
                    {t("dialog.additionalTags.more", {
                      count: template.tags.length - 3,
                    })}
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>
                      {t("dialog.additionalTags.title")}
                    </DialogTitle>
                    <DialogDescription className="flex flex-row flex-wrap gap-2">
                      {template.tags
                        .slice(3)
                        .map(
                          (tag: {
                            id: string;
                            tagText: string;
                            category?: string;
                            subcategory?: string;
                          }) => (
                            <Badge
                              key={tag.id}
                              variant="outline"
                              className={`${getBadgeStyles(tag.category, tag.subcategory)} text-xs font-medium border`}
                            >
                              {tag.tagText}
                            </Badge>
                          )
                        )}
                    </DialogDescription>
                  </DialogHeader>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}

function Templates() {
  const { t } = useTranslation("templates");
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const {
    data: templatesData,
    isLoading: templatesLoading,
    isError: templatesError,
    error: templatesErrorDetails,
    refetch,
  } = useUserTemplates();
  const { mutate: deleteTemplate } = useDeleteTemplate();
  const { toast } = useToast();

  const { isPilot } = useAppLinks();

  const handleDeleteTemplate = (templateId: string) => {
    deleteTemplate(templateId, {
      onSuccess: () => {
        toast({
          title: t("delete.success.title"),
          description: t("delete.success.description"),
        });
        refetch();
      },
      onError: (error) => {
        toast({
          title: t("delete.error.title"),
          description: error.message,
          variant: "destructive",
        });
      },
    });
  };

  const handleUploadSuccess = () => {
    refetch();
  };
  const is404 =
    templatesError && templatesErrorDetails?.message.includes("404");

  if (templatesError && !is404) {
    console.error("Template error:", templatesErrorDetails);
    toast({
      title: t("error.title"),
      description: t("error.description"),
      variant: "destructive",
    });
  }

  if (!isPilot) {
    return (
      <div className="flex items-center justify-center w-full h-full">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900">
            {t("pilot.title")}
          </h1>
          <p className="mt-2 text-gray-600">{t("pilot.description")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col w-full h-full min-h-screen px-6 pt-12 pb-8 overflow-auto">
      <div className="w-full max-w-6xl space-y-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="mb-2 text-3xl font-bold text-gray-900">
              {t("header.title")}
            </h1>
            <p className="text-lg text-gray-600">{t("header.description")}</p>
          </div>
          <Button onClick={() => setUploadModalOpen(true)} className="gap-2">
            <Plus className="w-4 h-4" />
            {t("upload.button")}
          </Button>
        </div>

        {/* Existing Templates */}
        <Card className="border-gray-200">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-xl text-gray-900">
                  {t("section.title")}
                </CardTitle>
                <CardDescription>{t("section.description")}</CardDescription>
              </div>
              {templatesLoading && (
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <div className="w-4 h-4 border-2 border-gray-300 rounded-full animate-spin border-t-transparent" />
                  {t("loading")}
                </div>
              )}
            </div>
            {is404 && (
              <Alert variant={is404 ? "default" : "destructive"}>
                <Warning className="w-4 h-4" />
                <AlertDescription className="flex items-center justify-between">
                  <span>
                    <Trans i18nKey={"error.title"} ns="templates">
                      We're having trouble loading your templates at the moment.
                      Please try again, or{" "}
                      <Link
                        to="mailto:support@arcology.io"
                        className="underline text-primary decoration-dashed"
                      >
                        contact support
                      </Link>{" "}
                      if the issue persists.
                    </Trans>
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => refetch()}
                    className="ml-4"
                  >
                    <ArrowClockwise className="w-4 h-4 mr-2" />
                    {t("error.retry")}
                  </Button>
                </AlertDescription>
              </Alert>
            )}
          </CardHeader>
          <CardContent>
            {templatesLoading ? (
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {Array.from({ length: 6 }).map((_, index) => (
                  <TemplateCardSkeleton key={index} />
                ))}
              </div>
            ) : templatesData && templatesData.templates.length > 0 ? (
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {templatesData.templates.map((template) => (
                  <TemplateCard
                    key={template.id}
                    template={template}
                    onDelete={handleDeleteTemplate}
                  />
                ))}
              </div>
            ) : null}
          </CardContent>
        </Card>

        {templatesData &&
          templatesData.templates.length === 0 &&
          !templatesLoading && (
            <Card className="border-gray-200 bg-gray-50/30">
              <CardContent className="pt-12 pb-12 text-center">
                <FileText className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                <h3 className="mb-2 text-lg font-semibold text-gray-900">
                  {t("empty.title")}
                </h3>
                <p className="mb-6 text-gray-600">{t("empty.description")}</p>
                <Button
                  onClick={() => setUploadModalOpen(true)}
                  className="gap-2"
                >
                  <Plus className="w-4 h-4" />
                  {t("upload.firstButton")}
                </Button>
              </CardContent>
            </Card>
          )}

        <TemplateUploadModal
          open={uploadModalOpen}
          onOpenChange={setUploadModalOpen}
          onSuccess={handleUploadSuccess}
        />
      </div>
    </div>
  );
}

export default Templates;
